<script setup lang="ts">
    import KFilter from "@/components/KFilter/KFilter.vue";
    import { computed, ref } from "vue";
    import { useStore } from "@/store";
    import { useKForm } from "@/composables/useKForm";

    import KButton from "@/components/Button.vue";

    import { generateSchema } from "@/components/forms/Form/schemaHelpers";
    import { ProfileDetail } from "@/types";

    const store = useStore();

    withDefaults(
        defineProps<{
            tableStateName?: string;
            name: string;
            keepOnClear?: string[];
            /**
             * Startvärde i formuläret
             */
            defaultValue?: {
                [key: string]: any;
            };
        }>(),
        {
            name: "besokslista",
        }
    );

    const emit = defineEmits<{
        (event: "submit", payload: { value: any }): void;
    }>();

    const filterRef = ref<InstanceType<typeof KFilter>>();

    const schema = generateSchema({
        addToAll: {
            ignoreEmptyData: true,
        },
        schema: [
            {
                component: "KText",
                name: "patient__personId",
                label: "PersonID",
            },
            {
                component: "KDateTimepicker",
                name: "bokat_datum__gte",
                label: "Bokat datum från",
            },
            {
                component: "KDateTimepicker",
                name: "bokat_datum__lte",
                label: "Bokat datum till",
            },
            {
                component: "ProfileAutocomplete",
                name: "besoksunderlag__vardpersonal",
                label: "Vårdpersonal",
                allowSearch: true,
                urlParams: {
                    orgUnit__only_permitted: true,
                    isActive: true,
                },
                autoPosition: true,
                multiple: true,
                showRoll: "yes",
            },
            {
                component: "BesokAutocomplete",
                name: "status",
                label: "Status",
                multiple: true,
                autoPosition: true,
                statusModuleName: "STATUS_BESOK",
            },
        ],
    });

    const personId = computed(() => store.getters["patientData/personId"]);

    const formId = computed(() => filterRef.value?.getFormId());

    const { formData } = useKForm({ id: formId });

    const currentProfile = computed<ProfileDetail>(() => store.getters["userData/currentProfile"]);

    const isFilterByAktivPatient = computed(() => formData.value?.patient__personId === personId.value);

    const isFilterByMinaBesok = computed(
        () =>
            formData.value?.besoksunderlag__vardpersonal === currentProfile.value?.id &&
            formData.value?.bokat_datum__gte?.substring(0, 10) === getCurrentDate()
    );

    const todayFiltersSet = computed(() => {
        const todayParams = getTodayParamsObject();
        return (
            formData.value?.bokat_datum__gte?.substring(0, 10) === todayParams.bokat_datum__gte &&
            formData.value?.bokat_datum__lte?.substring(0, 10) === todayParams.bokat_datum__lte
        );
    });

    const isFilterByBokade = computed(() => todayFiltersSet.value && formData.value?.status === "Bokad");
    const isFilterByAnkommit = computed(() => todayFiltersSet.value && formData.value?.status === "Ankommit");
    const isFilterByPagaende = computed(() => todayFiltersSet.value && formData.value?.status === "Pågående");
    const isFilterByToday = computed(() => todayFiltersSet.value && !formData.value?.status);

    const onFilter = ({ formData }: { formData: any }) => {
        emit("submit", { value: formData });
    };

    const getCurrentDate = () => new Date().toLocaleDateString("sv-SE");

    const getTodayParamsObject = () => {
        const today = getCurrentDate();
        return {
            bokat_datum__gte: today,
            bokat_datum__lte: today,
        };
    };

    defineExpose({
        onClearAll: () => filterRef.value?.onClearAll?.(),
    });
</script>

<template>
    <KFilter
        ref="filterRef"
        :name="name"
        :keepOnClear="keepOnClear"
        :tableStateName="tableStateName"
        :schema="schema"
        :defaultValue="defaultValue"
        @submit="onFilter"
    >
        <template #quick-actions="{ callback }">
            <KButton
                v-if="personId"
                type="button"
                variant="selectable"
                size="adaptable"
                :class="{ active: isFilterByAktivPatient }"
                @click="
                    callback({
                        values: {
                            patient__personId: personId,
                        },
                    })
                "
            >
                Aktiv patient
            </KButton>
            <KButton
                type="button"
                variant="selectable"
                size="adaptable"
                :class="{ active: isFilterByMinaBesok }"
                @click="
                    callback({
                        values: {
                            besoksunderlag__vardpersonal: currentProfile?.id || undefined,
                            bokat_datum__gte: getCurrentDate(),
                        },
                    })
                "
            >
                Mina besök
            </KButton>

            <KButton
                type="button"
                variant="selectable"
                size="adaptable"
                :class="{ active: isFilterByBokade }"
                @click="
                    callback({
                        values: {
                            status: 'Bokad',
                            ...getTodayParamsObject(),
                        },
                    })
                "
            >
                Bokade
            </KButton>
            <KButton
                type="button"
                variant="selectable"
                size="adaptable"
                :class="{ active: isFilterByAnkommit }"
                @click="callback({ values: { status: 'Ankommit', ...getTodayParamsObject() } })"
            >
                Ankommit
            </KButton>
            <KButton
                type="button"
                variant="selectable"
                size="adaptable"
                :class="{ active: isFilterByPagaende }"
                @click="callback({ values: { status: 'Pågående', ...getTodayParamsObject() } })"
            >
                Pågående
            </KButton>
            <KButton
                type="button"
                variant="selectable"
                size="adaptable"
                :class="{ active: isFilterByToday }"
                @click="callback({ values: { ...getTodayParamsObject() } })"
            >
                Idag
            </KButton>
        </template>
    </KFilter>
</template>
